<template>
  <div>
    <div class="d-flex align-center">
      <span
        class="v-label primary--text cursor-pointer"
        :class="{'font-weight-bold': !showMap}"
        @click="showMap = false">
        {{ $t('table' )}}
      </span>
      <v-switch dense color="primary" v-model="showMap" class="ml-3 mr-2"/>
      <span
        class="v-label primary--text cursor-pointer"
        :class="{'font-weight-bold': showMap}"
        @click="showMap = true">
        {{ $t('map') }}
      </span>
    </div>
    <div v-if="showMap">
      <route-map :route="route" :route-locations="routeLocations"/>
    </div>
    <div v-else>
      <div v-if="route.route_plan" class="d-flex align-center">
        <h5 class="mr-2 mb-0">
          <router-link :to="`/route-plan/${route.route_plan.id}`">
            <v-icon color="primary">mdi-calendar-check</v-icon>
            {{ $tc('route_plan') }} {{ route.route_plan.route_number }}
          </router-link>
        </h5>
        <v-chip small class="mx-2">
          <multi-time-display :time="route.route_plan.start" />
        </v-chip>
        {{ $t('to') }}
        <v-chip small class="mx-2">
          <multi-time-display :time="route.route_plan.stop" />
        </v-chip>
        <div v-if="!isEmpty(route.route_plan.unregistered_locations)" class="danger--text ml-4">
          <v-icon small color="danger">mdi-alert</v-icon>
          {{ $t('route_plan_contains_unregistered_locations') }}
          <router-link to="/locations/unregistered" class="ml-2">{{ $t('resolve') }}</router-link>
        </div>
      </div>
      <v-data-table
        id="route-locations-map-table"
        hide-default-footer
        disable-pagination
        disable-filtering
        disable-sort
        :items="routeLocations"
        :headers="tableFields"
        :item-class="rowClassCallback">
        <!-- Only a stateless button lives in the header: Vuetify renders the
             header `<th>`s without keys, so this node is destroyed and
             re-created whenever a column is hidden or shown. The configurator
             itself is mounted below, outside the table, so the open dialog
             survives a toggle. -->
        <template v-slot:header.__columnConfigurator>
          <v-btn
            icon
            small
            class="column-configurator-activator"
            :aria-label="$t('configure_columns')"
            @click.stop="openColumnConfigurator">
            <v-icon small>mdi-cog</v-icon>
          </v-btn>
        </template>
        <template v-slot:item.location="row">
          <!-- show link to location -->
          <vf-tooltip :text="getLocationTooltip(row.item)">
            <router-link
              v-if="!row.item.unregistered_location && user.can('update_location')"
              :to="`/location/update/${row.item.location.id}`"
              :class="{ 'danger--text': row.item.missed_stop, 'primary--text': row.item.unplanned_stop }">
              <span v-if="row.value.type === 'dc'">
                <v-icon :color="getLocationColor(row.item)">mdi-office-building</v-icon>
              </span>
              <span v-else>
                <v-avatar :color="getLocationColor(row.item)" size="25">
                  <span class="white--text">{{ row.index }}</span>
                </v-avatar>
              </span>
              <span class="ml-3">{{ row.value.label }}</span>
            </router-link>
            <!-- do not show location link -->
            <span v-else :class="{ 'danger--text': row.item.missed_stop, 'primary--text': row.item.unplanned_stop }">
            <span v-if="row.value.type === 'dc'">
              <v-icon :color="getLocationColor(row.item)">mdi-office-building</v-icon>
            </span>
            <span v-else>
              <v-avatar :color="getLocationColor(row.item)" size="25">
                <span class="white--text">{{ row.index }}</span>
              </v-avatar>
            </span>
            <span class="ml-3">{{ row.value.label }}</span>
          </span>
          </vf-tooltip>
          <!-- show button to create location off of unregistered code -->
          <v-btn
            v-if="row.item.unregistered_location && user.can('create_location')"
            fab text small
            :to="`/location/create?unregistered=1&code=${row.item.location.code}`">
            <v-icon color="primary">mdi-plus-circle</v-icon>
          </v-btn>
        </template>
        <template v-for='(column, index) in routeProductColumns' v-slot:[`item.${column.value}`]="row">
          <if-set :key="index" :value="getProductTemperature(row.item, column.route_product_id)">
            <template v-slot="scope">
              {{ temperatureToCustomerScale(scope.value) }} &deg; {{ customerContext.tempFormat() }}
            </template>
          </if-set>
        </template>
        <template v-slot:item.vehicle_arrival="row">
          <multi-time-display v-if="row.value" :time="row.value" />
          <span v-else class="text-danger text-nowrap">
            <vf-tooltip :text="$t('eta')">
              <v-icon>mdi-calendar-clock</v-icon>
            </vf-tooltip>
            <multi-time-display class="d-inline-block ml-1" :time="row.item.eta" />
          </span>
        </template>
        <template v-slot:item.vehicle_departure="row">
          <span class="text-nowrap">
            <multi-time-display v-if="row.value" :time="row.value" />
          </span>
        </template>
        <template v-slot:item.duration="row">
          <span v-if="!row.item.unregistered_location" class="text-nowrap">
            {{ row.item.getVehicleStopDuration() | humanizeDuration(({ numberOfSignificantParts: 2, separator: ', ' })) }}
          </span>
        </template>
      </v-data-table>
      <!-- Mounted outside the table on purpose (see the header slot above):
           this node's position never changes, so the dialog stays open while
           the user toggles and reorders columns. Renders nothing while the
           feature flag is off. -->
      <column-configurator
        ref="columnConfigurator"
        hide-activator
        :table-id="tableId"
        :columns="resolvedColumns"
        :default-columns="defaultColumnRegistry"
        @update:columns="onColumnsUpdate"
        @reset="onColumnsReset" />
    </div>
  </div>
</template>

<script>
import RouteMap from '@/components/route/RouteMap'
import RouteLocationRecord from '@/orm/models/RouteLocationRecord'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import IfSet from '@/components/shared/IfSet'
import VfTooltip from '@/components/shared/VfTooltip'
import ColumnConfigurator, { COLUMN_CONFIGURATOR_HEADER } from '@/components/shared/ColumnConfigurator'
import TableColumnPreferencesMixin from '@/components/mixins/TableColumnPreferencesMixin'
import { gmapApi } from 'vue2-google-maps'
import { forEach, each, get, isEmpty } from 'lodash'

/**
 * Persistence contract for this table (epic VF-4136). Both values are FIXED:
 * the id is what saved preferences are keyed by, so renaming it orphans every
 * preference already stored, and the integer version is bumped only for a
 * deliberate migration of the column registry below.
 */
export const ROUTE_LOCATIONS_TABLE_ID = 'RouteLocationsMap'
export const ROUTE_LOCATIONS_COLUMNS_VERSION = 1

export default {
  name: 'RouteLocationsMap',
  components: {
    RouteMap,
    MultiTimeDisplay,
    IfSet,
    VfTooltip,
    ColumnConfigurator
  },
  mixins: [
    CurrentUserMixin,
    DateTimeUtilsMixin,
    TemperatureMixin,
    TableColumnPreferencesMixin
  ],
  props: {
    route: Object
  },
  data () {
    return {
      isEmpty: isEmpty,
      showMap: false
    }
  },
  methods: {
    /**
     * Open the configurator modal from the gear button rendered in the table
     * header. The modal itself lives outside the table (see the template) so
     * it is not re-created when the header row changes length.
     */
    openColumnConfigurator () {
      this.$refs.columnConfigurator.openDialog()
    },
    rowClassCallback (row) {
      if (row.missed_stop) {
        return 'red lighten-5'
      }
      if (row.unplanned_stop) {
        return 'amber lighten-5'
      }
    },
    getLocationColor (routeLocation) {
      if (routeLocation.missed_stop) {
        return 'danger'
      }
      if (routeLocation.unplanned_stop) {
        return 'warning'
      }
      return 'primary'
    },
    /**
     * Get and format the product temperature at a given route location.
     * @param routeLocation
     * @param routeProductId
     * @returns {Number} The temperature to display
     */
    getProductTemperature (routeLocation, routeProductId) {
      return get(routeLocation, `simulated_temperatures_at_arrival.${routeProductId}`)
    },
    /**
     * Get the tooltip to display, given a route location.
     * @param routeLocation
     * @return String the tooltip to display
     */
    getLocationTooltip (routeLocation) {
      if (routeLocation.missed_stop) {
        return this.$tc('missed_stop')
      }
      if (routeLocation.unplanned_stop) {
        return this.$tc('unplanned_stop')
      }
      if (routeLocation.unregistered_location) {
        return this.$tc('unregistered_location')
      }
      return this.$t(routeLocation.location.type + '_location_type')
    }
  },
  computed: {
    /**
     * Expose imported gmapApi symbol as 'google' so we can use 'google.maps' object
     * inside Vue as we would normally in any other Javascript project.
     * (As recommended by the vue-google-maps documentation)
     */
    google: gmapApi,

    routeProductColumns () {
      const result = []
      each(this.route.route_products, rp => {
        result.push({
          value: `product_${rp.product_id}`,
          text: rp.product.name,
          route_product_id: rp.id
        })
      })
      return result
    },

    /**
     * @return String the id saved preferences for this table are keyed by
     */
    tableId () {
      return ROUTE_LOCATIONS_TABLE_ID
    },

    /**
     * This table's built-in column registry in default display order:
     * Location pinned first, one column per route product, then the three
     * timing columns. `id` always equals the Vuetify header `value`; product
     * ids are product_{product_id} (VF-4177) so a preference follows the
     * product across every route that carries it. `route_product_id` rides
     * along for the temperature lookup — the configurator preserves unknown
     * fields untouched.
     *
     * @return Array
     */
    defaultColumnRegistry () {
      const columns = [{
        id: 'location',
        label: this.$tc('location'),
        visible: true,
        primary: true,
        hideable: false,
        align: 'start'
      }]
      each(this.routeProductColumns, column => {
        columns.push({
          id: column.value,
          label: column.text,
          visible: true,
          primary: false,
          hideable: true,
          route_product_id: column.route_product_id
        })
      })
      columns.push({ id: 'vehicle_arrival', label: this.$t('arrival'), visible: true, primary: false, hideable: true })
      columns.push({ id: 'vehicle_departure', label: this.$t('departure'), visible: true, primary: false, hideable: true })
      columns.push({ id: 'duration', label: this.$t('duration'), visible: true, primary: false, hideable: true })
      return columns
    },

    /**
     * Override of TableColumnPreferencesMixin's computed: the single table
     * this component owns.
     *
     * @return Object
     */
    tableColumnDefaults () {
      return {
        [ROUTE_LOCATIONS_TABLE_ID]: {
          version: ROUTE_LOCATIONS_COLUMNS_VERSION,
          columns: this.defaultColumnRegistry
        }
      }
    },

    /**
     * The registry actually in effect: saved order/visibility merged into the
     * defaults, or the raw defaults when the feature flag is off.
     *
     * @return Array
     */
    resolvedColumns () {
      return this.resolvedTableColumns[ROUTE_LOCATIONS_TABLE_ID] || []
    },

    /**
     * Returns an array of field keys and names (translated) that should be
     * rendered in the locations table. The number of fields is variable based
     * on the number of products in the route, and on the user's column
     * preferences. The synthetic gear header is appended last, and only while
     * the feature is on.
     *
     * @return Array
     */
    tableFields () {
      const fields = []
      each(this.resolvedColumns, column => {
        if (!column.visible) {
          return
        }
        const field = { value: column.id, text: column.label }
        if (column.align) {
          field.align = column.align
        }
        fields.push(field)
      })
      if (this.configurableTableColumnsEnabled) {
        fields.push(COLUMN_CONFIGURATOR_HEADER)
      }
      return fields
    },

    /**
     * Interleave the route locations and unregistered locations so that
     * unregistered locations show up in the list of locations
     *
     * @reutrn array
     */
    routeLocations () {
      const routeLocations = []

      forEach(this.route.route_locations, (routeLocation) => {
        // Set up _rowVariant values on the routeLocations we display.
        // (Need to do this ahead of time before displaying the table data)
        if (routeLocation.missed_stop) {
          routeLocation._rowVariant = 'danger'
        } else if (routeLocation.unplanned_stop) {
          routeLocation._rowVariant = 'info'
        }

        routeLocations.push(routeLocation)
      })

      // The table in which route locations are listed expects a location object
      // however in the case of unregistered locations there is no location
      // (that's why it's unregistered). In order to facilitate rendering
      // unregsitered locations add a psudo location
      const routePlanLocations = get(this.route, 'route_plan.route_plan_locations', [])
      forEach(routePlanLocations, (routePlanLocation) => {
        if (routePlanLocation.location_id == null) {
          routePlanLocation.unregistered_location = true
          routePlanLocation._rowVariant = 'warning'
          routePlanLocation.location = this.$orm('Location').createRecord({
            id: undefined,
            label: routePlanLocation.location_code,
            code: routePlanLocation.location_code
          })
          routeLocations.push(routePlanLocation)
        }
      })

      // Sort by the route locations by vehicle arrival time or if unregistered
      // or missed location by eta, always put the DC departure as the first
      // location regardless of the eta (which may be before the DC departure)
      routeLocations.sort(RouteLocationRecord.sortByTypeAndArrivalTime)

      return routeLocations
    }
  }
}
</script>

<style scoped lang="scss">
  .map-locations-container {
    width: 300px;
  }
  .map-container {
    width: 100%;
  }

  .path-disclaimer {
    font-style: italic;
    font-size: smaller;
  }

  .route-plan-map {
    height: 400px;
  }

  /* Adjust the info window header sizes in google map a wee bit down */
  .route-plan-map ::v-deep(.gm-style-iw-d) h5 {
    font-size: 1rem;
  }
  .route-plan-map ::v-deep(.gm-style-iw-d) h6 {
    font-size: .75rem;
  }

  .route-stop {
    display: inline-block;
    text-align: center;
    margin-right: 10px;
  }

  .route-stop .svg-icon {
    padding-left: 1px;
    padding-right: 2px;
  }

  .route-stop .stop-icon {
    display: inline-block;
    font-size: .75rem;
    text-align: center;
    vertical-align: middle;
    background-color: #6991fd;
    border: 1.5px solid #333;
    color: black;

    height: 1.3rem;
    width: 1.3rem;
    border-radius: .65rem;
  }

  .table-danger .stop-icon {
    background-color: #fd7567;
  }

  .table-info .stop-icon {
    background-color: #17a2b8;
  }

  .route-plan-title {
    display: inline-block;
    vertical-align: middle;
    margin-bottom: 0;
  }

  .route-plan-start-stop-times {
    display: inline-block;
    vertical-align: middle;
    font-size: 1rem;
    line-height: 1rem;
  }
</style>
